'use client';

import { action, computed, makeObservable, observable } from 'mobx';

/** A single background task tracked in the status bar. */
export interface BackgroundTask {
  /** Stable unique id — doubles as the lookup key. */
  id: string;
  /** Human-readable label shown in the task list, e.g. "Downloading Kenney Platformer Kit". */
  label: string;
  /** Progress 0–100, or `undefined` for an indeterminate spinner. */
  progress?: number;
  /** Optional secondary line, e.g. "1.2 MB / 3.4 MB". */
  detail?: string;
}

/**
 * Plain MobX store for globally-visible background tasks.
 * Any subsystem can register a task for the duration of a long-running
 * operation and the StatusBar indicator will surface it automatically.
 *
 * Usage:
 * ```ts
 * backgroundTaskStore.addTask({ id: 'my-op', label: 'Doing something…' });
 * backgroundTaskStore.updateTask('my-op', { progress: 42, detail: '42%' });
 * backgroundTaskStore.removeTask('my-op');
 * ```
 */
class BackgroundTaskStore {
  tasks: Map<string, BackgroundTask> = new Map();

  constructor() {
    makeObservable(this, {
      tasks: observable,
      count: computed,
      list: computed,
      addTask: action,
      updateTask: action,
      removeTask: action,
    });
  }

  get count(): number {
    return this.tasks.size;
  }

  get list(): BackgroundTask[] {
    return Array.from(this.tasks.values());
  }

  /** Register a new task. Replaces any existing task with the same id. */
  addTask(task: BackgroundTask): void {
    this.tasks.set(task.id, task);
  }

  /** Patch a registered task. No-op if the id is not found. */
  updateTask(id: string, patch: Partial<Omit<BackgroundTask, 'id'>>): void {
    const existing = this.tasks.get(id);
    if (existing) {
      this.tasks.set(id, { ...existing, ...patch });
    }
  }

  /** Remove a task (e.g. on completion or cancellation). */
  removeTask(id: string): void {
    this.tasks.delete(id);
  }
}

export const backgroundTaskStore = new BackgroundTaskStore();
